import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Calculator, { Regions, TransactionWithComparisonTaxValues, PortfolioComparisonItem } from '@capintel/instruments-lib';

import { Dispatch } from '@/redux';
import { setTaxAnalysisData, setTaxAnalysisHoldingsData } from '@/redux/modules/comparison';

import { TaxAnalysisInputValues } from '../useTaxAnalysisInputs/useTaxAnalysisInputs';
import ComparisonItem from '@/types/comparison-item';
import { PortfolioTaxMetrics } from '@/types/tax-analysis';

interface UseTaxAnalysisDataParams {
  selectedItem?: ComparisonItem;
  taxInputs: TaxAnalysisInputValues;
  targetComparisonItem?: ComparisonItem;
  isPortfolioEditorOpen: boolean;
  readOnly?: boolean;
}

/**
 * Hook to manage tax analysis calculations and dispatch results to Redux.
 * Tax metrics are stored in Redux and consumed from there by components.
 * Skips recalculation when the portfolio editor is open to prevent excessive reruns.
 */
export const useTaxAnalysisData = ({ selectedItem, taxInputs, targetComparisonItem, isPortfolioEditorOpen, readOnly = false }: UseTaxAnalysisDataParams) => {
  const dispatch = useDispatch<Dispatch>();

  // Use ref to store cached data without causing rerenders
  const lastCalculationRef = useRef<string>('');
  const wasEditorOpenRef = useRef<boolean>(false);

  // Create a key to check if we need to recalculate
  // Include holdings count and a simple hash to detect portfolio changes
  // Temporary workaround for recalculating when we close portfolio editor with new changes.
  const holdingsKey =
    selectedItem?.holdings?.map(h => `${h.id}:${h.units ?? h.weight ?? ''}:${h.taxSettings?.costBasis ?? ''}:${h.taxSettings?.purchaseDate ?? ''}`).join(',') ||
    '';
  const targetHoldingsKey = targetComparisonItem?.holdings?.map(h => `${h.id}:${h.units ?? h.weight ?? ''}`).join(',') || '';

  useEffect(() => {
    // Don't run calculator if readOnly
    if (readOnly) {
      return;
    }

    // If portfolio editor is open, don't recalculate
    if (isPortfolioEditorOpen) {
      wasEditorOpenRef.current = true;
      return;
    }

    // If editor was just closed, force recalculation by clearing the cache
    if (wasEditorOpenRef.current && !isPortfolioEditorOpen) {
      wasEditorOpenRef.current = false;
      lastCalculationRef.current = '';
    }

    if (!selectedItem || !selectedItem.holdings) {
      // Clear the cache when there's no selected item so recalculation happens when one is selected
      lastCalculationRef.current = '';
      return;
    }

    const shortTermRate = taxInputs.shortTermCapitalGainsRate;
    const longTermRate = taxInputs.longTermCapitalGainsRate;

    if (!shortTermRate || !longTermRate) {
      return;
    }

    const calculationKey = [
      selectedItem._id,
      holdingsKey,
      targetComparisonItem?._id || '',
      targetHoldingsKey,
      shortTermRate,
      longTermRate,
      taxInputs.shortTermYTDGain,
      taxInputs.shortTermYTDLoss,
      taxInputs.longTermYTDGain,
      taxInputs.longTermYTDLoss,
      taxInputs.shortTermCarriedForwardLoss,
      taxInputs.longTermCarriedForwardLoss,
    ].join('|');

    // Skip if we already calculated for this exact combination
    if (lastCalculationRef.current === calculationKey) {
      return;
    }

    lastCalculationRef.current = calculationKey;

    try {
      const comparisonResult = Calculator.getInstance()
        .portfolio()
        .calculateTransitionTaxes(
          selectedItem as PortfolioComparisonItem,
          targetComparisonItem as PortfolioComparisonItem | undefined,
          Regions.US,
          {
            shortTermCapitalGainsTaxRate: shortTermRate / 100,
            longTermCapitalGainsTaxRate: longTermRate / 100,
          },
          new Date(),
        )
        .withTaxImplications({
          shortTermYTDGain: taxInputs.shortTermYTDGain ?? 0,
          shortTermYTDLoss: taxInputs.shortTermYTDLoss ?? 0,
          longTermYTDGain: taxInputs.longTermYTDGain ?? 0,
          longTermYTDLoss: taxInputs.longTermYTDLoss ?? 0,
          shortTermCarriedForwardLoss: taxInputs.shortTermCarriedForwardLoss ?? 0,
          longTermCarriedForwardLoss: taxInputs.longTermCarriedForwardLoss ?? 0,
        })
        .calculate();

      if (targetComparisonItem) {
        const taxMetrics: PortfolioTaxMetrics = {
          taxImplications: comparisonResult.taxImplications,
          changelog: comparisonResult.changelog,
        };

        dispatch(setTaxAnalysisData(selectedItem._id, taxMetrics));
      }

      const holdingsTaxMetrics = comparisonResult.transactions.map((transaction: TransactionWithComparisonTaxValues) => ({
        holdingId: transaction.holdingId,
        taxMetrics: {
          currentAllocation: transaction.currentAllocation,
          estimatedRealizedGainLoss: transaction.estimatedRealizedGainLoss,
          marketValue: transaction.marketValue,
          longTermUnrealizedGainLoss: transaction.unrealizedGainOrLoss?.LONG_TERM,
          totalUnrealizedGain: (transaction.unrealizedGainOrLoss?.SHORT_TERM || 0) + (transaction.unrealizedGainOrLoss?.LONG_TERM || 0),
          shortTermUnrealizedGainLoss: transaction.unrealizedGainOrLoss?.SHORT_TERM,
          taxCostToReachTarget: transaction.taxCostToGetToTargetAllocation,
          valueChange: transaction.valueChange,
        },
        lots: transaction.lotsWithTaxMetrics?.length ? transaction.lotsWithTaxMetrics : undefined,
      }));

      dispatch(setTaxAnalysisHoldingsData(selectedItem._id, holdingsTaxMetrics));
    } catch (error) {
      console.error('Error calculating tax analysis:', error);
    }
  }, [
    selectedItem?._id,
    targetComparisonItem?._id,
    taxInputs.shortTermCapitalGainsRate,
    taxInputs.longTermCapitalGainsRate,
    taxInputs.shortTermYTDGain,
    taxInputs.shortTermYTDLoss,
    taxInputs.longTermYTDGain,
    taxInputs.longTermYTDLoss,
    taxInputs.shortTermCarriedForwardLoss,
    taxInputs.longTermCarriedForwardLoss,
    isPortfolioEditorOpen,
    holdingsKey,
    targetHoldingsKey,
    dispatch,
  ]);
};
